import { useEffect, useRef, useState } from 'react';
import { DT } from '../../theme/designTokens';
import { AVATAR_KEYS, avatarAssetPath, normalizeAvatarKey, isValidAvatarKey } from '../../config/avatars';
import { PlayerAvatar } from './PlayerAvatar';
import { useTranslation } from '../../i18n/react';

/**
 * Player Identity System V1 — preset-avatar picker (Settings).
 *
 * Controlled component mirroring CountryPicker's contract and anatomy: the
 * trigger shows the authoritative current selection (validated key or null),
 * `onSelect` fires when the user commits a choice — including null via the
 * "No avatar" entry. The caller owns persistence; on a failed save it keeps
 * passing the prior `value`, so the picker never shows an unconfirmed
 * selection. Options come exclusively from the 50-key catalog — there is no
 * free entry, upload, or URL input of any kind.
 */
export interface AvatarPickerProps {
  value: string | null;
  onSelect: (key: string | null) => void;
  /** Disables interaction (e.g. while a save is in flight). */
  disabled?: boolean;
  /** The user's display name — drives the initial fallback previews. */
  displayName: string;
  id?: string;
  /**
   * Trigger presentation (Settings V1 addition). 'chip' (default) is the
   * original avatar+label button; 'circle' renders the Figma Settings
   * gold-gradient circular preview. The dropdown, options, aria contract,
   * and persistence behavior are identical in both variants.
   */
  variant?: 'chip' | 'circle';
}

export function AvatarPicker({ value, onSelect, disabled = false, displayName, id = 'avatar-picker', variant = 'chip' }: AvatarPickerProps) {
  const { t } = useTranslation('settings');
  const normalized = normalizeAvatarKey(value);
  const current = isValidAvatarKey(normalized) ? normalized : null;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close on outside click (anchored-dropdown precedent, same as CountryPicker).
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function commit(key: string | null) {
    setOpen(false);
    if (key !== current) onSelect(key);
  }

  // Label pattern keyed on the stable avatar key's numeric suffix — the key
  // itself (avatar_NN) is an identifier and is never translated or altered.
  const currentLabel = current
    ? t('avatarPicker.avatarN', { n: current.split('_')[1] })
    : t('avatarPicker.noAvatar');

  return (
    <div ref={rootRef} style={{ position: 'relative', display: 'inline-block' }}
      onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
    >
      {/* Trigger — shows the authoritative current selection. */}
      {variant === 'circle' ? (
        // Figma Settings anatomy: the current avatar IS the button — a
        // gold-gradient circle (initial fallback) or the selected preset.
        <button
          type="button"
          id={id}
          onClick={() => { if (!disabled) setOpen(o => !o); }}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('avatarPicker.trigger', { label: currentLabel })}
          style={{
            display:      'inline-flex',
            padding:      0,
            background:   'transparent',
            border:       'none',
            borderRadius: '50%',
            cursor:       disabled ? 'default' : 'pointer',
            opacity:      disabled ? 0.6 : 1,
            boxShadow:    '0 15px 40px -15px rgba(201,168,76,0.45)',
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <PlayerAvatar
            avatarKey={current}
            name={displayName}
            size={72}
            loading="eager"
            initialStyle={{
              background: 'linear-gradient(104.72deg, #F0D78C 0%, #C9A84C 50%, #8A7028 100%)',
              border:     `1px solid ${DT.color.gold}`,
              color:      '#0B0B0B',
              fontWeight: 700,
            }}
          />
        </button>
      ) : (
      <button
        type="button"
        id={id}
        onClick={() => { if (!disabled) setOpen(o => !o); }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('avatarPicker.trigger', { label: currentLabel })}
        style={{
          display:      'inline-flex',
          alignItems:   'center',
          gap:          8,
          padding:      '5px 12px 5px 5px',
          background:   'rgba(255,255,255,0.04)',
          border:       `1px solid ${DT.color.border}`,
          borderRadius: 8,
          color:        current ? DT.color.textPrimary : DT.color.textMuted,
          fontFamily:   DT.font.sans,
          fontSize:     13,
          cursor:       disabled ? 'default' : 'pointer',
          opacity:      disabled ? 0.6 : 1,
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        <PlayerAvatar avatarKey={current} name={displayName} size={28} loading="eager" />
        <span>{currentLabel}</span>
        <span aria-hidden="true" style={{ fontSize: 10, color: DT.color.textMuted }}>▾</span>
      </button>
      )}

      {open && (
        <div
          style={{
            position:     'absolute',
            top:          'calc(100% + 6px)',
            right:        0,
            zIndex:       50,
            width:        300,
            background:   '#181818',
            border:       `1px solid ${DT.color.border}`,
            borderRadius: 10,
            boxShadow:    '0 12px 32px rgba(0,0,0,0.5)',
            overflow:     'hidden',
          }}
        >
          <div
            id={`${id}-listbox`}
            role="listbox"
            aria-label={t('avatarPicker.listAria')}
            style={{
              maxHeight:      320,
              overflowY:      'auto',
              scrollbarWidth: 'none',
              padding:        10,
            }}
          >
            {/* Clear option — back to the display-name initial. */}
            <button
              type="button"
              role="option"
              aria-selected={current === null}
              onClick={() => commit(null)}
              style={{
                display:    'flex',
                alignItems: 'center',
                gap:        10,
                width:      '100%',
                padding:    '6px 8px',
                marginBottom: 8,
                background: current === null ? 'rgba(201,168,76,0.10)' : 'transparent',
                border:     'none',
                borderRadius: 8,
                cursor:     'pointer',
                textAlign:  'left',
                fontFamily: DT.font.sans,
                fontSize:   13,
                fontStyle:  'italic',
                color:      DT.color.textMuted,
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <PlayerAvatar avatarKey={null} name={displayName} size={28} loading="eager" />
              <span style={{ flex: 1 }}>{t('avatarPicker.clearOption')}</span>
              {current === null && <span aria-hidden="true" style={{ color: DT.color.gold }}>✓</span>}
            </button>

            {/* The 50 catalog presets — a compact grid. */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
              {AVATAR_KEYS.map(key => {
                const selected = key === current;
                return (
                  <button
                    key={key}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    aria-label={t('avatarPicker.avatarN', { n: key.split('_')[1] })}
                    onClick={() => commit(key)}
                    style={{
                      display:        'flex',
                      alignItems:     'center',
                      justifyContent: 'center',
                      padding:        2,
                      background:     'transparent',
                      border:         selected ? `2px solid ${DT.color.gold}` : '2px solid transparent',
                      borderRadius:   '50%',
                      cursor:         'pointer',
                      WebkitTapHighlightColor: 'transparent',
                    }}
                  >
                    <img
                      src={avatarAssetPath(key)!}
                      alt=""
                      width={44}
                      height={44}
                      loading="lazy"
                      draggable={false}
                      style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
